import { useState, useEffect } from 'react'
import { ArrowRightLeft, Search, Tag, X, AlertTriangle, Link, Trash2, CheckCircle, Loader } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

function formatDuration(secs) {
  if (!secs) return '—'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.round(secs % 60)
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatBytes(bytes) {
  if (!bytes) return ''
  if (bytes > 1e9) return (bytes / 1e9).toFixed(1) + ' GB'
  if (bytes > 1e6) return (bytes / 1e6).toFixed(1) + ' MB'
  return (bytes / 1e3).toFixed(0) + ' KB'
}

export default function MigrateFromCFModal({ onClose, onComplete }) {
  const { api } = useAuth()

  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterUIDs, setTagFilterUIDs] = useState(null)
  const [checked, setChecked] = useState(new Set())
  const [mode, setMode] = useState('copy') // 'copy' | 'migrate'
  const [swapLibrary, setSwapLibrary] = useState(true) // auto-swap content library URLs

  // Processing state
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [results, setResults] = useState([])
  const [status, setStatus] = useState('')

  // Pending CF cleanup: [{ uid, title, assetId, muxStatus }]
  const [pendingCleanup, setPendingCleanup] = useState([])
  const [pollingCleanup, setPollingCleanup] = useState(false)

  // Load CF Stream videos
  useEffect(() => {
    loadVideos()
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [])

  async function loadVideos() {
    setLoading(true)
    try {
      const params = new URLSearchParams({ per_page: 100, search: searchTerm })
      const data = await api(`/videos?${params}`)
      let list = []
      if (Array.isArray(data)) list = data
      else if (Array.isArray(data.result)) list = data.result
      else if (data.result?.videos) list = data.result.videos
      else if (data.videos) list = data.videos
      else if (data.data) list = data.data
      setVideos(list)
    } catch (e) {
      setStatus(`Error loading videos: ${e.message}`)
    }
    setLoading(false)
  }

  // Tag filter
  useEffect(() => {
    if (!activeTag) { setTagFilterUIDs(null); return }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => setTagFilterUIDs(d.stream_uids || []))
      .catch(() => setTagFilterUIDs([]))
  }, [activeTag])

  // Search on enter
  function handleSearchSubmit(e) {
    e.preventDefault()
    loadVideos()
  }

  // Filter videos
  let filtered = videos
  if (tagFilterUIDs !== null) {
    filtered = filtered.filter(v => tagFilterUIDs.includes(v.uid))
  }

  function toggleVideo(uid) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(uid) ? next.delete(uid) : next.add(uid)
      return next
    })
  }

  function toggleAll() {
    if (checked.size === filtered.length) {
      setChecked(new Set())
    } else {
      setChecked(new Set(filtered.map(v => v.uid)))
    }
  }

  async function handleMigrate() {
    const selected = filtered.filter(v => checked.has(v.uid))
    if (!selected.length) return

    const deleteAfter = mode === 'migrate'

    // Load existing Mux assets to check for duplicate titles
    setStatus('Checking for duplicates in Mux...')
    let muxTitles = new Set()
    try {
      const muxData = await api('/mux/assets?limit=100')
      const muxAssets = muxData.data || muxData || []
      for (const a of muxAssets) {
        const t = (a.meta?.title || a.passthrough || '').toLowerCase().trim()
        if (t) muxTitles.add(t)
      }
    } catch {}

    // Check which selected videos already exist in Mux
    const dupes = selected.filter(v => {
      const t = (v.meta?.name || '').toLowerCase().trim()
      return t && muxTitles.has(t)
    })

    let skipDupes = false
    if (dupes.length > 0) {
      const dupeNames = dupes.slice(0, 10).map(v => `  • ${v.meta?.name}`).join('\n')
      const more = dupes.length > 10 ? `\n  ...and ${dupes.length - 10} more` : ''
      const nonDupeCount = selected.length - dupes.length

      // First ask: skip or include?
      const choice = confirm(
        `${dupes.length} of ${selected.length} video${selected.length > 1 ? 's' : ''} already exist in Mux:\n\n${dupeNames}${more}\n\nClick OK to SKIP duplicates and ${mode === 'migrate' ? 'migrate' : 'copy'} only the ${nonDupeCount} new one${nonDupeCount !== 1 ? 's' : ''}.\nClick Cancel to include duplicates (creates copies in Mux).`
      )
      skipDupes = choice

      if (skipDupes && nonDupeCount === 0) {
        setStatus('All selected videos already exist in Mux — nothing to migrate.')
        return
      }
    }

    const toProcess = skipDupes
      ? selected.filter(v => !dupes.includes(v))
      : selected

    if (deleteAfter && !confirm(
      `Migrate ${toProcess.length} video${toProcess.length > 1 ? 's' : ''} to Mux?\n\nThis will DELETE them from CF Stream after copying. This cannot be undone.`
    )) return

    setProcessing(true)
    setProgress({ done: 0, total: toProcess.length })
    setResults([])

    // Add skipped dupes to results first
    const newResults = []
    if (skipDupes) {
      for (const v of dupes) {
        newResults.push({ uid: v.uid, title: v.meta?.name || v.uid, ok: false, error: 'Skipped — duplicate in Mux', isDupe: true, skipped: true })
      }
      setResults([...newResults])
    }

    for (let i = 0; i < toProcess.length; i++) {
      const video = toProcess[i]
      const title = video.meta?.name || video.uid
      const isDupe = muxTitles.has(title.toLowerCase().trim())
      setStatus(`${deleteAfter ? 'Migrating' : 'Copying'} ${i + 1}/${selected.length}: ${title}...`)

      try {
        const res = await api('/mux/migrate-from-cf', {
          method: 'POST',
          body: JSON.stringify({
            uid: video.uid,
            title,
            deleteAfter,
            swapContentLibrary: swapLibrary,
          })
        })
        newResults.push({
          uid: video.uid,
          title,
          ok: true,
          assetId: res.assetId,
          muxHlsUrl: res.muxHlsUrl,
          pendingCfDelete: res.pendingCfDelete,
          contentLibraryEntries: res.contentLibraryEntries || [],
          playlistEntries: res.playlistEntries || [],
          swapped: res.swapped || [],
          isDupe,
        })
      } catch (e) {
        newResults.push({ uid: video.uid, title, ok: false, error: e.message })
      }

      setProgress({ done: i + 1, total: selected.length })
      setResults([...newResults])
    }

    const successes = newResults.filter(r => r.ok).length
    const skipped = newResults.filter(r => r.skipped).length
    const failures = newResults.filter(r => !r.ok && !r.skipped).length

    // Populate pending CF cleanup for migrate mode
    if (deleteAfter) {
      const pending = newResults
        .filter(r => r.ok && r.pendingCfDelete)
        .map(r => ({ uid: r.uid, title: r.title, assetId: r.assetId, muxStatus: 'processing' }))
      if (pending.length) {
        setPendingCleanup(pending)
        pollMuxStatuses(pending)
      }
      setStatus(`✅ ${successes} copied to Mux — review CF cleanup below${skipped ? ` (${skipped} skipped)` : ''}${failures ? ` (${failures} failed)` : ''}`)
    } else {
      setStatus(`✅ ${successes} copied to Mux${skipped ? `, ${skipped} skipped (duplicate)` : ''}${failures ? `, ${failures} failed` : ''}`)
    }
    setProcessing(false)
    if (successes > 0) onComplete()
  }

  // Poll Mux asset statuses for pending cleanup items
  async function pollMuxStatuses(items) {
    setPollingCleanup(true)
    const updated = [...items]
    let allReady = false

    while (!allReady) {
      await new Promise(r => setTimeout(r, 8000))
      allReady = true
      for (let i = 0; i < updated.length; i++) {
        if (updated[i].muxStatus === 'ready') continue
        try {
          const data = await api(`/mux/assets/${updated[i].assetId}`)
          const asset = data.data || data
          updated[i].muxStatus = asset.status === 'ready' ? 'ready' : 'processing'
        } catch {}
        if (updated[i].muxStatus !== 'ready') allReady = false
      }
      setPendingCleanup([...updated])
    }
    setPollingCleanup(false)
  }

  // Delete a single CF Stream video
  async function handleDeleteCF(uid) {
    if (!confirm('Delete this video from CF Stream? This cannot be undone.')) return
    try {
      await api(`/videos/${uid}`, { method: 'DELETE' })
      setPendingCleanup(prev => prev.filter(p => p.uid !== uid))
      setStatus(prev => prev) // keep existing status
    } catch (e) {
      setStatus(`Error deleting ${uid}: ${e.message}`)
    }
  }

  // Delete all ready items from CF Stream
  async function handleDeleteAllReady() {
    const ready = pendingCleanup.filter(p => p.muxStatus === 'ready')
    if (!ready.length) return
    if (!confirm(`Delete ${ready.length} video${ready.length > 1 ? 's' : ''} from CF Stream?\n\nMux copies are confirmed ready. This cannot be undone.`)) return

    let deleted = 0
    for (const item of ready) {
      try {
        await api(`/videos/${item.uid}`, { method: 'DELETE' })
        deleted++
      } catch {}
    }
    setPendingCleanup(prev => prev.filter(p => p.muxStatus !== 'ready'))
    setStatus(`✅ ${deleted} deleted from CF Stream`)
  }

  return (
    <div className="mux-modal-backdrop" onClick={onClose}>
      <div className="mux-modal mux-migrate-modal" onClick={e => e.stopPropagation()}>
        <div className="mux-modal-header">
          <h3><ArrowRightLeft size={16} /> Migrate from Cloudflare Stream</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="mux-modal-body">
          {/* Search + Tag filter */}
          <div className="mux-migrate-filters">
            <form onSubmit={handleSearchSubmit} className="mux-migrate-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search CF Stream videos..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button type="submit">Search</button>
            </form>
            <div className="mux-migrate-tag-filter">
              <Tag size={13} />
              <select value={activeTag} onChange={e => setActiveTag(e.target.value)}>
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {filtered.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={toggleAll} style={{ whiteSpace: 'nowrap' }}>
                {checked.size === filtered.length ? 'Deselect All' : `Select All (${filtered.length})`}
              </button>
            )}
          </div>

          {/* Mode toggle */}
          <div className="mux-migrate-mode">
            <label className={mode === 'copy' ? 'active' : ''}>
              <input type="radio" name="mode" value="copy" checked={mode === 'copy'} onChange={() => setMode('copy')} />
              Copy to Mux <span className="mux-migrate-mode-desc">(keep original in CF Stream)</span>
            </label>
            <label className={mode === 'migrate' ? 'active' : ''}>
              <input type="radio" name="mode" value="migrate" checked={mode === 'migrate'} onChange={() => setMode('migrate')} />
              Migrate to Mux <span className="mux-migrate-mode-desc">(copy, then review + delete from CF when Mux is ready)</span>
            </label>
          </div>

          {/* Content library swap option */}
          <div className="mux-migrate-swap-option">
            <label>
              <input type="checkbox" checked={swapLibrary} onChange={e => setSwapLibrary(e.target.checked)} />
              <Link size={12} /> Auto-update Content Library
              <span className="mux-migrate-mode-desc"> — swap CF HLS URLs to Mux HLS for any matching entries</span>
            </label>
          </div>

          {/* Video list */}
          <div className="mux-restore-list">
            {loading ? (
              <div className="mux-restore-empty">Loading CF Stream videos...</div>
            ) : filtered.length === 0 ? (
              <div className="mux-restore-empty">No videos found</div>
            ) : (
              filtered.map(video => {
                const isChecked = checked.has(video.uid)
                const title = video.meta?.name || '(untitled)'
                return (
                  <div
                    key={video.uid}
                    className={`mux-restore-item ${isChecked ? 'selected' : ''}`}
                    onClick={() => toggleVideo(video.uid)}
                  >
                    <input type="checkbox" checked={isChecked} onChange={() => {}} style={{ marginRight: 8, flexShrink: 0 }} />
                    <span className="mux-restore-name">{title}</span>
                    <span className="mux-migrate-meta">
                      {formatDuration(video.duration)}
                      {video.size ? ` · ${formatBytes(video.size)}` : ''}
                    </span>
                  </div>
                )
              })
            )}
          </div>

          {/* Progress / Results */}
          {processing && (
            <div className="mux-migrate-progress">
              <div className="mux-migrate-progress-bar">
                <div className="mux-migrate-progress-fill" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
              <span>{progress.done} / {progress.total}</span>
            </div>
          )}

          {results.length > 0 && (
            <div className="mux-restore-results" style={{ marginTop: 8 }}>
              {results.map((r, i) => (
                <div key={i} className={`mux-restore-result ${r.ok ? 'ok' : r.skipped ? 'skipped' : 'fail'}`}>
                  <span className="mux-restore-result-icon">{r.ok ? '✓' : r.skipped ? '—' : '✕'}</span>
                  <span className="mux-restore-name">{r.title}</span>
                  {r.ok ? (
                    <span className="mux-restore-result-info">
                      <span className="mux-restore-size">{r.assetId}</span>
                      {r.isDupe && <span className="mux-migrate-dupe"><AlertTriangle size={10} /> duplicate</span>}
                      {r.deleted && <span className="mux-migrate-deleted">deleted from CF</span>}
                      {r.contentLibraryEntries?.length > 0 && (
                        <span className="mux-migrate-lib-info">
                          {r.swapped?.length > 0 ? (
                            <span className="mux-migrate-swapped">
                              <Link size={10} /> {r.swapped.length} content library {r.swapped.length === 1 ? 'entry' : 'entries'} updated to Mux
                            </span>
                          ) : (
                            <span className="mux-migrate-lib-warn">
                              <AlertTriangle size={10} /> {r.contentLibraryEntries.length} content library {r.contentLibraryEntries.length === 1 ? 'entry uses' : 'entries use'} this CF URL
                            </span>
                          )}
                        </span>
                      )}
                      {r.playlistEntries?.length > 0 && (
                        <span className="mux-migrate-lib-info">
                          <span className="mux-migrate-lib-warn">
                            <AlertTriangle size={10} /> {r.playlistEntries.length} playlist {r.playlistEntries.length === 1 ? 'item uses' : 'items use'} this CF URL
                            ({[...new Set(r.playlistEntries.map(e => e.channel_name))].join(', ')})
                          </span>
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="mux-restore-error">{r.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Action bar */}
          {checked.size > 0 && !processing && pendingCleanup.length === 0 && (
            <div className="mux-restore-action" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {checked.size} video{checked.size > 1 ? 's' : ''} selected
                </span>
                <button className="btn btn-primary" onClick={handleMigrate}>
                  {mode === 'migrate'
                    ? `Migrate ${checked.size} to Mux`
                    : `Copy ${checked.size} to Mux`}
                </button>
              </div>
              {status && <div className="mux-restore-status">{status}</div>}
            </div>
          )}

          {/* Pending CF Cleanup */}
          {pendingCleanup.length > 0 && (
            <div className="mux-migrate-cleanup">
              <div className="mux-migrate-cleanup-header">
                <h4><Trash2 size={14} /> Pending CF Stream Cleanup</h4>
                {(() => {
                  const readyCount = pendingCleanup.filter(p => p.muxStatus === 'ready').length
                  return readyCount > 0 ? (
                    <button className="btn btn-ghost btn-sm mux-migrate-delete-ready" onClick={handleDeleteAllReady}>
                      <Trash2 size={12} /> Delete {readyCount} Ready from CF
                    </button>
                  ) : pollingCleanup ? (
                    <span className="mux-migrate-polling"><Loader size={12} className="spinning" /> Checking Mux status...</span>
                  ) : null
                })()}
              </div>
              <div className="mux-migrate-cleanup-list">
                {pendingCleanup.map(item => (
                  <div key={item.uid} className={`mux-migrate-cleanup-item ${item.muxStatus}`}>
                    <span className="mux-migrate-cleanup-title">{item.title}</span>
                    <span className={`mux-migrate-cleanup-status mux-cleanup-${item.muxStatus}`}>
                      {item.muxStatus === 'ready' ? (
                        <><CheckCircle size={11} /> Mux Ready</>
                      ) : (
                        <><Loader size={11} className="spinning" /> Processing</>
                      )}
                    </span>
                    {item.muxStatus === 'ready' && (
                      <button className="aq-btn-sm aq-btn-remove" onClick={() => handleDeleteCF(item.uid)}>
                        <Trash2 size={11} /> Delete from CF
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {status && <div className="mux-restore-status" style={{ marginTop: 8 }}>{status}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
